import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  performanceMetricsService,
  ArticlePerformanceRow,
  ContentTypeFilter,
  MetricsFetchStatus,
  SortBy,
  SortOrder,
} from '../../../../services/performanceMetricsService';
import { ArticleDetailPanel } from './ArticleDetailPanel';
import { LinkGraph, QueryGraph } from './LinkGraph';
import { PerformanceTableView } from './PerformanceTableView';
import { ClusterCardView } from './ClusterCardView';
import { PerformanceOverview } from './PerformanceOverview';
import { CtaPerformance } from './CtaPerformance';
import { generateAIExport } from './exportForAI';

type ViewMode = 'overview' | 'table' | 'pages-overview' | 'links' | 'keywords' | 'by-pillar' | 'by-funnel' | 'cta-perf';

export type CohortFilter = 'all' | '2025' | '2026' | 'growth-x' | 'pipeline';

export const COHORT_LABELS: Record<CohortFilter, string> = {
  all: 'All',
  '2025': '2025',
  '2026': '2026',
  'growth-x': 'Growth X',
  pipeline: 'Pipeline',
};

export const COHORT_TOOLTIPS: Record<CohortFilter, string> = {
  all: 'All articles, no date filter',
  '2025': 'Articles published before January 1, 2026',
  '2026': 'Articles published on or after January 1, 2026',
  'growth-x': 'Articles published between January 12, 2026 and March 15, 2026',
  pipeline: 'Articles published on or after March 15, 2026',
};

export function filterByCohort<T extends { published_at?: string | null }>(rows: T[], cohort: CohortFilter): T[] {
  if (cohort === 'all') return rows;
  return rows.filter((r) => {
    const pub = r.published_at;
    if (!pub) return false;
    const d = pub.slice(0, 10);
    switch (cohort) {
      case '2025': return d < '2026-01-01';
      case '2026': return d >= '2026-01-01';
      case 'growth-x': return d >= '2026-01-12' && d < '2026-03-15';
      case 'pipeline': return d >= '2026-03-15';
      default: return true;
    }
  });
}

const PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PerformanceDashboard: React.FC = () => {
  const [rows, setRows] = useState<ArticlePerformanceRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsStatus, setMetricsStatus] = useState<MetricsFetchStatus | null>(null);

  // Controls
  const [sortBy, setSortBy] = useState<SortBy>('clicks');
  const [order, setOrder] = useState<SortOrder>('desc');
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [contentType, setContentType] = useState<ContentTypeFilter>('post');

  // Indexation refresh
  const [refreshingIndex, setRefreshingIndex] = useState(false);
  const [indexRefreshMsg, setIndexRefreshMsg] = useState<string | null>(null);

  // Side panel
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('overview');

  // Cohort filter
  const [cohort, setCohort] = useState<CohortFilter>('all');

  // Export
  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    setExporting(true);
    await new Promise(r => setTimeout(r, 50));
    try {
      const ct = (viewMode === 'table' && contentType === 'page') || viewMode === 'pages-overview' ? 'page' : 'post';
      const resp = await performanceMetricsService.list({ days: 28, sortBy: 'clicks', order: 'desc', limit: 2000, contentType: ct as ContentTypeFilter });
      const filtered = cohort === 'all' ? resp.metrics : filterByCohort(resp.metrics, cohort);
      const label = ct === 'page' ? 'Website Pages' : 'Blog';
      const md = generateAIExport(filtered, label);
      const blob = new Blob([md], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `talaera-${ct}-performance-${new Date().toISOString().slice(0, 10)}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [viewMode, contentType, cohort]);

  // Debounce search input
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setOffset(0);
    }, 250);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search]);

  // Fetch the page whenever any control changes
  const loadPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await performanceMetricsService.list({
        days: 28,
        sortBy,
        order,
        limit: PAGE_SIZE,
        offset,
        q: debouncedSearch || undefined,
        contentType,
      });
      setRows(resp.metrics);
      setTotalCount(resp.total_count);
    } catch (e: any) {
      setError(e?.message || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [sortBy, order, offset, debouncedSearch, contentType]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // Load fetch status once on mount
  useEffect(() => {
    (async () => {
      try {
        const s = await performanceMetricsService.status();
        setMetricsStatus(s);
      } catch {
        // Non-fatal
      }
    })();
  }, []);

  const handleSortClick = (column: SortBy) => {
    if (sortBy === column) {
      setOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(column);
      const ascByDefault = column === 'position' || column === 'title' || column === 'indexed' || column === 'category';
      setOrder(ascByDefault ? 'asc' : 'desc');
    }
    setOffset(0);
  };

  const handleContentTypeChange = (ct: ContentTypeFilter) => {
    setContentType(ct);
    setOffset(0);
  };

  const handleRefreshIndexation = async () => {
    setRefreshingIndex(true);
    setIndexRefreshMsg(null);
    try {
      const result = await performanceMetricsService.refreshIndexation();
      setIndexRefreshMsg(
        `Checked ${result.checked} articles · ${result.indexed} indexed · ${result.not_indexed} not indexed · ${result.failed} failed`
      );
      loadPage();
    } catch (e: any) {
      setIndexRefreshMsg(`Failed: ${e?.message || 'unknown error'}`);
    } finally {
      setRefreshingIndex(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Performance</h1>
          <p className="text-sm text-gray-500 mt-1">
            Search Console metrics over the trailing 28 days. Daily fetch runs at 06:00 UTC.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Cohort filter */}
          <div className="flex rounded border border-gray-300 overflow-hidden">
            {(Object.entries(COHORT_LABELS) as [CohortFilter, string][]).map(([key, label]) => (
              <button
                key={key}
                onClick={() => { setCohort(key); setOffset(0); }}
                title={COHORT_TOOLTIPS[key]}
                className={`text-xs px-2.5 py-1 ${
                  cohort === key
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-1.5"
          >
            {exporting && <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-gray-500" />}
            {exporting ? 'Generating...' : 'Export for AI'}
          </button>
          <button
            onClick={handleRefreshIndexation}
            disabled={refreshingIndex}
            className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {refreshingIndex ? 'Checking…' : 'Refresh indexation'}
          </button>
        </div>
      </div>

      {indexRefreshMsg && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-3 py-2 rounded mb-3 text-xs">
          {indexRefreshMsg}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {/* Unified tabs */}
      <div className="flex items-center gap-1 mb-3 border-b border-gray-200">
        {([
          ['overview', 'Blog Overview'],
          ['posts', 'Blog Articles'],
          ['pages-overview', 'Website Overview'],
          ['pages', 'Website Pages'],
          ['by-pillar', 'Blog by Category'],
          ['by-funnel', 'Blog by Funnel'],
          ['links', 'Link Map'],
          ['keywords', 'Keywords'],
          ['cta-perf', 'CTA Perf'],
        ] as const).map(([mode, label]) => {
          const active = (mode === 'posts' && viewMode === 'table' && contentType === 'post')
            || (mode === 'pages' && viewMode === 'table' && contentType === 'page')
            || (mode !== 'posts' && mode !== 'pages' && viewMode === (mode as string));
          return (
            <button
              key={mode}
              onClick={() => {
                if (mode === 'posts') { setViewMode('table'); handleContentTypeChange('post'); }
                else if (mode === 'pages') { setViewMode('table'); handleContentTypeChange('page'); }
                else if (mode === 'overview') { setViewMode('overview'); }
                else if (mode === 'pages-overview') { setViewMode('pages-overview'); }
                else { setViewMode(mode); }
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? 'border-cyan-500 text-cyan-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {viewMode === 'overview' ? (
        <PerformanceOverview cohort={cohort} contentType="post" />
      ) : viewMode === 'pages-overview' ? (
        <PerformanceOverview cohort={cohort} contentType="page" />
      ) : viewMode === 'by-pillar' ? (
        <ClusterCardView groupBy="pillar" cohort={cohort} />
      ) : viewMode === 'by-funnel' ? (
        <ClusterCardView groupBy="funnel_stage" cohort={cohort} />
      ) : viewMode === 'links' ? (
        <LinkGraph />
      ) : viewMode === 'keywords' ? (
        <QueryGraph />
      ) : viewMode === 'cta-perf' ? (
        <CtaPerformance />
      ) : (
        <PerformanceTableView
          rows={cohort === 'all' ? rows : filterByCohort(rows, cohort)}
          totalCount={totalCount}
          loading={loading}
          metricsStatus={metricsStatus}
          sortBy={sortBy}
          order={order}
          offset={offset}
          search={search}
          onSortClick={handleSortClick}
          onOffsetChange={setOffset}
          onSearchChange={setSearch}
          onRowClick={setSelectedUrl}
        />
      )}

      {/* Side panel */}
      {selectedUrl && (
        <ArticleDetailPanel url={selectedUrl} onClose={() => setSelectedUrl(null)} />
      )}
    </div>
  );
};
